// src/pages/settings/CompanionsPage.tsx
//
// Companions — /settings/companions (Reforma UX §6.10, COMP-01..04).
// Extraída da antiga SettingsPage (aba `channels`), reorganizada em 2 abas:
//
// - **Companions**: cards com nome, slug, avatar (se houver) e logos dos
//   canais conectados + contagem de sessões ativas por canal
//   (CompanionListItem.channels — §7.7; fallback graceful pré-restart:
//   deriva o chip Telegram de telegram_bot_token_set, sem contagem).
//   Clicar no card abre a modal de detalhes do agente (mesma do §6.6).
// - **Canais**: Telegram (disponível) com os bots conectados
//   (avatar/nome/@username, "Trocar bot" / "Desconectar") + "Conectar
//   companion". WhatsApp · Slack · Discord = "em breve" com empty state.

import { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import type { CompanionListItem } from '@/lib/api';
import {
  Send,
  Loader2,
  MessageCircle,
  Plus,
  RefreshCw,
  Bot,
  CheckCircle2,
  Unplug,
  X,
  Slack,
  Gamepad2,
} from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { CreateAssetModal } from '@/components/CreateAssetModal';
import { AgentDetailModal } from '@/components/agents/AgentDetailModal';
import { agentInitials } from '@/components/agents/agent-constants';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Alert } from '@/components/ui/Alert';
import '@/styles/wf-agents.css'; // chrome: .dhead/.dnewbtn
import '@/styles/wf-settings.css';

/** Logo/cor por canal conhecido (wireframe §11 — .comp-chip ch-tg/ch-wa/...). */
const CHANNEL_META: Record<string, { label: string; icon: typeof Send; chClass: string }> = {
  telegram: { label: 'Telegram', icon: Send, chClass: 'ch-tg' },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, chClass: 'ch-wa' },
  slack: { label: 'Slack', icon: Slack, chClass: 'ch-sl' },
  discord: { label: 'Discord', icon: Gamepad2, chClass: 'ch-dc' },
};

/** Avatar do companion (wireframe .comp-av 46px / .conn-av 34px): imagem
 *  (avatar_url §7.5) ou iniciais. */
function CompanionAvatar({ c, size = 'md' }: { c: CompanionListItem; size?: 'sm' | 'md' }) {
  const cls = size === 'sm' ? 'conn-av' : 'comp-av';
  return (
    <div className={cls}>
      {c.avatar_url ? (
        <img src={c.avatar_url} alt={c.name} className="h-full w-full object-cover" />
      ) : (
        agentInitials(c.name)
      )}
    </div>
  );
}

export function CompanionsPage() {
  const [tab, setTab] = useState<string>('companions');
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Companions (multi-bot — Phase 3)
  const [companions, setCompanions] = useState<CompanionListItem[]>([]);
  const [companionsLoading, setCompanionsLoading] = useState(true);
  const [companionBotEditId, setCompanionBotEditId] = useState<string | null>(null);
  const [companionBotTokenDraft, setCompanionBotTokenDraft] = useState('');
  const [companionBotSaving, setCompanionBotSaving] = useState(false);
  const [companionDisconnectingId, setCompanionDisconnectingId] = useState<string | null>(null);
  // COMP-04 — confirmação de desconectar (wireframe dlgDiscBot)
  const [disconnectConfirmId, setDisconnectConfirmId] = useState<string | null>(null);

  // COMP-03 — modal de detalhes (mesma do agente, §6.6)
  const [detailRef, setDetailRef] = useState<string | null>(null);

  // Criar companion por escopo (CreateAssetModal — F8)
  const [companionScopeOpen, setCompanionScopeOpen] = useState(false);
  const [companionMsg, setCompanionMsg] = useState<string | null>(null);

  // Modal "Conectar companion" da aba CANAIS (Breno 11/06): conecta um
  // companion EXISTENTE ainda desconectado do canal — select + bot token.
  // Criação de companion NÃO mora aqui (é o "Novo companion" da aba
  // Companions, que abre o wizard de escopo direto).
  const [connectCompanionOpen, setConnectCompanionOpen] = useState(false);
  const [selectedExistingId, setSelectedExistingId] = useState('');
  const [newCompanionToken, setNewCompanionToken] = useState('');
  const [newCompanionSaving, setNewCompanionSaving] = useState(false);
  const [newCompanionError, setNewCompanionError] = useState('');

  const companionsWithoutBot = companions.filter((c) => c.is_active && !c.telegram_bot_token_set);
  const companionsWithBot = companions.filter((c) => c.telegram_bot_token_set);

  const fetchCompanions = useCallback(async () => {
    try {
      const data = await api.listCompanions();
      setCompanions(data);
    } catch (err) {
      // Non-fatal: keep prior state and notify.
      const msg = err instanceof Error ? err.message : 'Erro ao listar companions';
      setFeedback({ type: 'error', message: msg });
    } finally {
      setCompanionsLoading(false);
    }
  }, []);

  useEffect(() => { fetchCompanions(); }, [fetchCompanions]);

  // --- Companion bot handlers ---

  function openCompanionBotEdit(id: string) {
    setCompanionBotEditId(id);
    setCompanionBotTokenDraft('');
  }

  function closeCompanionBotEdit() {
    setCompanionBotEditId(null);
    setCompanionBotTokenDraft('');
  }

  async function handleCompanionBotSave() {
    if (!companionBotEditId) return;
    if (!companionBotTokenDraft.trim()) {
      setFeedback({ type: 'error', message: 'Cole o token do bot do Telegram.' });
      return;
    }
    setCompanionBotSaving(true);
    try {
      await api.updateCompanion(companionBotEditId, {
        telegram_bot_token: companionBotTokenDraft.trim(),
      });
      await fetchCompanions();
      closeCompanionBotEdit();
      setFeedback({ type: 'success', message: 'Bot do Telegram vinculado ao companion.' });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao atualizar companion.',
      });
    } finally {
      setCompanionBotSaving(false);
    }
  }

  async function handleCompanionBotDisconnect(id: string) {
    setCompanionDisconnectingId(id);
    setDisconnectConfirmId(null);
    try {
      await api.updateCompanion(id, { telegram_bot_token: null });
      await fetchCompanions();
      setFeedback({ type: 'success', message: 'Bot desconectado deste companion.' });
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err instanceof Error ? err.message : 'Erro ao desconectar bot.',
      });
    } finally {
      setCompanionDisconnectingId(null);
    }
  }

  /** Aba Companions → "Novo companion": wizard de criação por escopo, direto. */
  function openCreateCompanionWizard() {
    setCompanionScopeOpen(true);
  }

  /** Aba Canais → "Conectar companion": select dos companions DESCONECTADOS
   *  do canal + token. Sem nenhum fluxo de criação aqui. */
  function openConnectCompanion() {
    setConnectCompanionOpen(true);
    setSelectedExistingId('');
    setNewCompanionToken('');
    setNewCompanionError('');
  }

  function closeConnectCompanion() {
    setConnectCompanionOpen(false);
    setNewCompanionError('');
  }

  async function handleLinkExistingBot() {
    if (!selectedExistingId) {
      setNewCompanionError('Selecione um companion.');
      return;
    }
    const token = newCompanionToken.trim();
    if (!token) {
      setNewCompanionError('Token do bot é obrigatório.');
      return;
    }
    setNewCompanionSaving(true);
    setNewCompanionError('');
    try {
      await api.updateCompanion(selectedExistingId, { telegram_bot_token: token });
      await fetchCompanions();
      setConnectCompanionOpen(false);
      setFeedback({
        type: 'success',
        message: 'Companion conectado ao Telegram.',
      });
    } catch (err) {
      setNewCompanionError(
        err instanceof Error ? err.message : 'Erro ao vincular bot.'
      );
    } finally {
      setNewCompanionSaving(false);
    }
  }

  /** Canais conectados do companion (§7.7) com fallback graceful pré-restart:
   *  sem `channels` no payload, deriva Telegram de telegram_bot_token_set
   *  (sem contagem de sessões). */
  function companionChannels(c: CompanionListItem): Array<{ channel: string; active_sessions: number | null }> {
    if (Array.isArray(c.channels) && c.channels.length > 0) {
      return c.channels.map((ch) => ({ channel: ch.channel, active_sessions: ch.active_sessions }));
    }
    if (c.telegram_bot_token_set) {
      return [{ channel: 'telegram', active_sessions: null }];
    }
    return [];
  }

  const editingCompanion = companionBotEditId
    ? companions.find((c) => c.id === companionBotEditId) ?? null
    : null;
  const disconnectingCompanion = disconnectConfirmId
    ? companions.find((c) => c.id === disconnectConfirmId) ?? null
    : null;

  return (
    <div>
      <PageHeader
        title="Companions"
        description="Seus companions e os canais em que cada um está conectado. Conecte bots na aba Canais."
      />

      {feedback && (
        <Alert
          variant={feedback.type === 'success' ? 'success' : 'destructive'}
          className="mt-4"
          dismissible
          onDismiss={() => setFeedback(null)}
        >
          {feedback.message}
        </Alert>
      )}

      <div className="mt-4">
        {/* Abas Companions · Canais (wireframe §11 .ctabs) */}
        <div className="ctabs">
          <button
            type="button"
            className={tab === 'companions' ? 'on' : undefined}
            onClick={() => setTab('companions')}
          >
            Companions
          </button>
          <button
            type="button"
            className={tab === 'channels' ? 'on' : undefined}
            onClick={() => setTab('channels')}
          >
            Canais
          </button>
        </div>

        <div>
          {/* ─── Aba Companions (COMP-02/03) ─────────────────────────── */}
          {tab === 'companions' && (
            <div>
              <div className="dhead">
                <button type="button" className="dnewbtn" onClick={openCreateCompanionWizard}>
                  <Plus className="ic" aria-hidden />
                  Novo companion
                </button>
              </div>
              {companionsLoading ? (
                <div className="comp-list">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-28 rounded-xl" />
                  ))}
                </div>
              ) : companions.length === 0 ? (
                <Card className="p-6">
                  <EmptyState
                    icon={Bot}
                    title="Nenhum companion criado"
                    description="Crie seu primeiro companion para conversar pelos canais."
                    action={{ label: 'Novo companion', onClick: openCreateCompanionWizard }}
                  />
                </Card>
              ) : (
                <div className="comp-list">
                  {companions.map((c) => {
                    const channels = companionChannels(c);
                    return (
                      <div
                        key={c.id}
                        className="compc"
                        onClick={() => setDetailRef(c.slug || c.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setDetailRef(c.slug || c.id);
                          }
                        }}
                        aria-label={`Detalhes do companion ${c.name}`}
                      >
                        <div className="compc-top">
                          <CompanionAvatar c={c} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="comp-name">
                              {c.name}
                              {c.is_default && (
                                <span className="wf-badge wf-badge-accent" style={{ marginLeft: 7 }}>
                                  padrão
                                </span>
                              )}
                              {!c.is_active && (
                                <span className="wf-badge wf-badge-neutral" style={{ marginLeft: 7 }}>
                                  inativo
                                </span>
                              )}
                            </div>
                            <div className="comp-slug">{c.slug}</div>
                          </div>
                        </div>
                        <div className="comp-ch">
                          {channels.length === 0 ? (
                            <span className="comp-chip" style={{ color: 'var(--wf-muted)' }}>
                              Sem canal conectado
                            </span>
                          ) : (
                            channels.map((ch) => {
                              const meta = CHANNEL_META[ch.channel] ?? {
                                label: ch.channel,
                                icon: MessageCircle,
                                chClass: '',
                              };
                              const Icon = meta.icon;
                              return (
                                <span key={ch.channel} className={`comp-chip ${meta.chClass}`}>
                                  <Icon className="ic" aria-hidden />
                                  {meta.label}
                                  {ch.active_sessions != null && (
                                    <>
                                      {' '}
                                      <b>{ch.active_sessions}</b>{' '}
                                      {ch.active_sessions === 1 ? 'sessão' : 'sessões'}
                                    </>
                                  )}
                                </span>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ─── Aba Canais (COMP-04 — wireframe .chan-grid/.chanc/.conn-row) ── */}
          {tab === 'channels' && (
            <div className="chan-grid">
              {/* Telegram — disponível */}
              <div className="chanc">
                <div className="chanc-h">
                  <div className="chan-ic ch-tg">
                    <Send className="ic" aria-hidden />
                  </div>
                  <div>
                    <div className="chan-name">Telegram</div>
                    <div className="comp-slug" style={{ color: 'var(--wf-success-text)' }}>
                      disponível
                    </div>
                  </div>
                </div>

                {companionsLoading ? (
                  <div className="space-y-2">
                    {[1, 2].map((i) => (
                      <Skeleton key={i} className="h-14 rounded-lg" />
                    ))}
                  </div>
                ) : companionsWithBot.length === 0 ? (
                  <div className="chan-empty">
                    <Bot className="ic" aria-hidden />
                    <div>Nenhum companion conectado · crie/conecte o primeiro.</div>
                  </div>
                ) : (
                  <div className="conn-list">
                    {companionsWithBot.map((c) => {
                      const disconnecting = companionDisconnectingId === c.id;
                      return (
                        <div key={c.id} className="conn-row">
                          <CompanionAvatar c={c} size="sm" />
                          <div className="conn-main">
                            <div className="conn-name">
                              <span className="conn-dot" aria-hidden />
                              {c.name}
                            </div>
                            {c.telegram_bot_username && (
                              <div className="conn-sub">@{c.telegram_bot_username}</div>
                            )}
                          </div>
                          <div className="conn-acts">
                            <button
                              type="button"
                              onClick={() => openCompanionBotEdit(c.id)}
                              aria-label={`Trocar bot do Telegram de ${c.name}`}
                            >
                              <RefreshCw className="ic" aria-hidden />
                              Trocar bot
                            </button>
                            <button
                              type="button"
                              className="dc"
                              onClick={() => setDisconnectConfirmId(c.id)}
                              disabled={disconnecting}
                              aria-label={`Desconectar bot de ${c.name}`}
                              title="Desconectar bot do Telegram deste companion"
                            >
                              {disconnecting ? (
                                <Loader2 className="ic animate-spin" aria-hidden />
                              ) : (
                                <X className="ic" aria-hidden />
                              )}
                              Desconectar
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <button
                  type="button"
                  className="dnewbtn"
                  style={{ margin: '12px 0 0', width: '100%', justifyContent: 'center' }}
                  onClick={openConnectCompanion}
                >
                  <Plus className="ic" aria-hidden />
                  Conectar companion
                </button>
              </div>

              {/* WhatsApp · Slack · Discord — em breve (wireframe .chanc.soon) */}
              {([
                { name: 'WhatsApp', icon: MessageCircle, chClass: 'ch-wa' },
                { name: 'Slack', icon: Slack, chClass: 'ch-sl' },
                { name: 'Discord', icon: Gamepad2, chClass: 'ch-dc' },
              ] as const).map(({ name, icon: Icon, chClass }) => (
                <div key={name} className="chanc soon" title="Em construção">
                  <div className="chanc-h">
                    <div className={`chan-ic ${chClass}`}>
                      <Icon className="ic" aria-hidden />
                    </div>
                    <div>
                      <div className="chan-name">{name}</div>
                      <div className="comp-slug">em breve</div>
                    </div>
                  </div>
                  <div className="chan-empty">
                    <Bot className="ic" aria-hidden />
                    <div>Integração em breve.</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* COMP-03 — modal de detalhes (mesma do agente §6.6) */}
      {detailRef && (
        <AgentDetailModal
          open={!!detailRef}
          onOpenChange={(open) => { if (!open) setDetailRef(null); }}
          agentRef={detailRef}
          onError={(message) => setFeedback({ type: 'error', message })}
        />
      )}

      {/* Modal Trocar/Conectar bot de um companion específico (wireframe dlgConnBot) */}
      {editingCompanion && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
        >
          <Card className="w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-raios-text">
                {editingCompanion.telegram_bot_token_set
                  ? `Trocar bot · ${editingCompanion.name}`
                  : `Conectar ${editingCompanion.name} ao Telegram`}
              </h3>
              <button type="button" onClick={closeCompanionBotEdit} className="text-raios-muted hover:text-raios-text" aria-label="Fechar">
                <X className="h-4 w-4" />
              </button>
            </div>

            <ol className="text-xs text-raios-muted space-y-1 list-decimal list-inside">
              <li>
                Abra o{' '}
                <button
                  type="button"
                  onClick={() => window.open('https://t.me/BotFather', '_blank')}
                  className="text-raios-accent-text hover:underline cursor-pointer"
                >
                  @BotFather
                </button>{' '}
                no Telegram e crie um bot com{' '}
                <code className="font-mono text-raios-accent-text bg-raios-elevated px-1 py-0.5 rounded">/newbot</code>
              </li>
              <li>Copie o token que o BotFather devolve</li>
              <li>Cole abaixo</li>
            </ol>

            <Input
              type="password"
              value={companionBotTokenDraft}
              onChange={(e) => setCompanionBotTokenDraft(e.target.value.replace(/\s/g, ''))}
              placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz..."
              label="Token do Bot"
            />

            <div className="flex items-center gap-2">
              <Button
                variant="brand"
                size="sm"
                onClick={handleCompanionBotSave}
                loading={companionBotSaving}
                disabled={!companionBotTokenDraft.trim()}
              >
                <CheckCircle2 className="h-3.5 w-3.5" />
                Salvar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={closeCompanionBotEdit}
                disabled={companionBotSaving}
              >
                <X className="h-3.5 w-3.5" />
                Cancelar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Confirmação de desconectar (wireframe dlgDiscBot) */}
      {disconnectingCompanion && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
        >
          <Card className="w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-raios-text">Desconectar companion</h3>
            <p className="text-sm text-raios-text-secondary">
              Tem certeza que deseja desconectar <strong>{disconnectingCompanion.name}</strong> do Telegram?
              As sessões ativas serão encerradas.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setDisconnectConfirmId(null)}>
                Cancelar
              </Button>
              <Button
                variant="outline"
                className="text-raios-danger-text"
                onClick={() => handleCompanionBotDisconnect(disconnectingCompanion.id)}
              >
                <Unplug className="h-3.5 w-3.5" />
                Desconectar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Add Companion Modal — step=choice: 2 cards; step=existing: dropdown + token */}
      {/* Conectar companion ao canal (aba Canais — Breno 11/06): SELECT dos
          companions existentes ainda DESCONECTADOS do canal + bot token.
          Criação de companion fica na aba Companions (wizard). */}
      {connectCompanionOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="connect-companion-title"
        >
          <Card className="w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 id="connect-companion-title" className="text-lg font-semibold text-raios-text">
                Conectar companion ao Telegram
              </h3>
              <button
                type="button"
                onClick={closeConnectCompanion}
                className="text-raios-muted hover:text-raios-text"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {companionsWithoutBot.length === 0 ? (
              <>
                <p className="text-sm text-raios-muted">
                  Todos os companions já estão conectados ao Telegram. Para conectar um
                  novo, crie-o primeiro na aba <strong>Companions</strong>.
                </p>
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={closeConnectCompanion}>
                    Fechar
                  </Button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm text-raios-muted">
                  Selecione um companion ainda não conectado e cole o token do bot
                  (peça ao @BotFather com /newbot).
                </p>

                {newCompanionError && (
                  <p className="text-xs text-raios-danger-text bg-raios-danger/10 rounded px-3 py-2">
                    {newCompanionError}
                  </p>
                )}

                <div>
                  <label
                    htmlFor="companion-select"
                    className="block text-xs font-medium text-raios-text-secondary mb-1.5"
                  >
                    Companion
                  </label>
                  <select
                    id="companion-select"
                    value={selectedExistingId}
                    onChange={(e) => setSelectedExistingId(e.target.value)}
                    className="w-full bg-raios-card border border-raios-border rounded-md text-sm text-raios-text px-3 py-2 focus:outline-none focus:ring-2 focus:ring-raios-accent/40 focus:border-raios-accent"
                  >
                    <option value="">— Selecione —</option>
                    {companionsWithoutBot.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.slug}){c.is_default ? ' · padrão' : ''}
                      </option>
                    ))}
                  </select>
                </div>

                <Input
                  type="password"
                  label="Token do Bot"
                  value={newCompanionToken}
                  onChange={(e) => setNewCompanionToken(e.target.value.replace(/\s/g, ''))}
                  placeholder="1234567890:ABCdefGHIjklMNOpqrsTUVwxyz..."
                  helperText="Peça ao @BotFather com /newbot. Formato: <id>:<secret>"
                />

                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={closeConnectCompanion} disabled={newCompanionSaving}>
                    Cancelar
                  </Button>
                  <Button
                    variant="brand"
                    onClick={handleLinkExistingBot}
                    loading={newCompanionSaving}
                    disabled={!selectedExistingId || !newCompanionToken.trim()}
                  >
                    Conectar
                  </Button>
                </div>
              </>
            )}
          </Card>
        </div>
      )}

      {/* F8 — criar companion por ESCOPO (substitui o wizard /agents/new). Bind de bot
          fica no fluxo de link-bot existente. */}
      <CreateAssetModal
        type="agent"
        forceArchetype="companion"
        open={companionScopeOpen}
        onClose={() => setCompanionScopeOpen(false)}
        onSubmitted={(msg) => {
          setCompanionMsg(msg);
          setTimeout(() => setCompanionMsg(null), 8000);
        }}
      />
      {companionMsg && (
        <div className="fixed bottom-4 right-4 z-50 max-w-md rounded-lg border border-border bg-background px-4 py-3 shadow-lg text-sm">
          {companionMsg}
        </div>
      )}
    </div>
  );
}
